import {v4 as uuidv4} from 'uuid';
import { randomIdSuffix } from '@/shared/utils/ids';

/**
 * Session Persistence Utilities
 *
 * Manages persistent identity across page reloads using browser storage:
 * - **localStorage**: Player ID, Peer ID, Awareness state
 * - **IndexedDB**: Y.Doc state (handled by y-indexeddb)
 *
 * ## Why Persistence Matters
 *
 * Without persistence, every page reload creates a "new user":
 * - ❌ New player ID → Appears as different player
 * - ❌ New peer ID → Reconnection churn in peer list
 * - ❌ Lost state → All cards, health, piles reset
 *
 * With persistence:
 * - ✅ Same player ID → Seamless reconnection
 * - ✅ Same peer ID → Stable peer identity
 * - ✅ Restored state → Cards and game state persist
 *
 * ## Usage Example
 *
 * ```typescript
 * // In index.ts
 * const playerId = getOrCreatePlayerId(); // "player-abc123" (persists)
 * const peerId = getOrCreatePeerId();     // UUID (persists)
 *
 * const provider = new WebRTCProvider(yDoc, {
 *   roomName,
 *   peerId, // Pass persistent peer ID
 * });
 * ```
 *
 * @see WebRTCProvider.ts for how these are used
 */

const STORAGE_KEYS = {
  PLAYER_ID: 'aura:playerId',
  PLAYER_NAME: 'aura:playerName',
  PLAYER_COLOR: 'aura:playerColor',
  AWARENESS_STATE: 'aura:awarenessState',
  PEER_ID: 'aura:peerId',
} as const;

export interface AwarenessState {
  name?: string;
  playerId?: string;
  [key: string]: unknown;
}

/**
 * Get or create a persistent player ID. Player ID is used by game logic. Peer ID is used by networking logic.
 * This ensures the same player ID is used across page reloads
 */
export function getOrCreatePlayerId(): string {
  let playerId = localStorage.getItem(STORAGE_KEYS.PLAYER_ID);

  if (!playerId) {
    playerId = `player-${randomIdSuffix(7)}`;
    localStorage.setItem(STORAGE_KEYS.PLAYER_ID, playerId);
  }

  return playerId;
}

/**
 * Get the player's chosen display name from localStorage, if they've set one.
 * The name follows the user across rooms and reloads. The Yjs player state is
 * seeded from this on init; returns null when the user has never renamed.
 */
export function getStoredPlayerName(): string | null {
  return localStorage.getItem(STORAGE_KEYS.PLAYER_NAME);
}

/**
 * Persist the player's chosen display name to localStorage.
 */
export function setStoredPlayerName(name: string): void {
  localStorage.setItem(STORAGE_KEYS.PLAYER_NAME, name);
}

/**
 * Get the player's chosen identity color from localStorage, if they've picked
 * one. Same contract as the display name: localStorage is authoritative for
 * what the *local* player chose, because Player's constructor reseeds its own
 * Yjs state on every boot — without this, a color picked in Settings would be
 * overwritten by the playerId-derived default on the next reload. Returns null
 * when the user has never picked one.
 */
export function getStoredPlayerColor(): string | null {
  return localStorage.getItem(STORAGE_KEYS.PLAYER_COLOR);
}

/**
 * Persist the player's chosen identity color to localStorage.
 */
export function setStoredPlayerColor(color: string): void {
  localStorage.setItem(STORAGE_KEYS.PLAYER_COLOR, color);
}

/**
 * Get or create a persistent peer ID for WebRTC connections. Peer ID is used by networking logic. Player ID is used by game logic
 * This helps prevent peer identity changes on reconnection
 */
export function getOrCreatePeerId(): string {
  let peerId = localStorage.getItem(STORAGE_KEYS.PEER_ID);

  if (!peerId) {
    peerId = uuidv4();
    localStorage.setItem(STORAGE_KEYS.PEER_ID, peerId);
  }

  return peerId;
}

/**
 * Save awareness state to localStorage
 */
export function saveAwarenessState(state: AwarenessState): void {
  try {
    const { name, playerId } = state; // identity only — never persist transient fields (drag, cursor)
    localStorage.setItem(STORAGE_KEYS.AWARENESS_STATE, JSON.stringify({ name, playerId }));
  } catch (error) {
    console.error('Failed to save awareness state:', error);
  }
}

/**
 * Restore awareness state from localStorage
 */
export function restoreAwarenessState(): AwarenessState | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.AWARENESS_STATE);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Failed to restore awareness state:', error);
    return null;
  }
}

/**
 * Set up automatic awareness state persistence
 * Saves awareness state before page unload
 * @returns Cleanup function to remove the event listener
 */
export function setupAwarenessStatePersistence(getState: () => AwarenessState | null): () => void {
  const handleBeforeUnload = () => {
    const state = getState();
    if (state) {
      saveAwarenessState(state);
    }
  };

  window.addEventListener('beforeunload', handleBeforeUnload);

  // Return cleanup function
  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };
}

/**
 * Clear all persisted session data
 * Useful for testing or "start fresh" functionality
 */
export function clearPersistedSession(): void {
  localStorage.removeItem(STORAGE_KEYS.PLAYER_ID);
  localStorage.removeItem(STORAGE_KEYS.PLAYER_NAME);
  localStorage.removeItem(STORAGE_KEYS.AWARENESS_STATE);
  localStorage.removeItem(STORAGE_KEYS.PEER_ID);
}